<template>
  <div>
    <div>
      <FilterCard :applied-filter-count="appliedFilterCount">
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Inactivated Date"
              label-for="inactivated-date"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['inactivated_at']"
                  :opens="'right'"
                  @update="updateDateRange('inactivated_at', $event)"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Merchant Name"
              label-for="merchant-name"
            >
              <b-form-input
                id="merchant-name"
                v-model="filters['name']"
                placeholder="Enter Merchant Name"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Inactivation Method"
              label-for="inactivation-method"
            >
              <v-select
                v-model="filters['inactive_status']"
                :options="inactivationMethodOptions"
                :reduce="option => option.value"
                label="label"
                placeholder="Select Method"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Last Dispatched Date"
              label-for="last-dispatched-date"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['last_order_dispatched_at']"
                  :opens="'left'"
                  @update="updateDateRange('last_order_dispatched_at', $event)"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1">
          <b-col>
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </FilterCard>
    </div>
    <div>
      <b-card no-body>
        <div class="m-2">
          <b-row>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
            >
              <div>
                <ReportTableButtons
                  :items="items"
                  :json_fieldz="json_fields"
                  :name="`${downloadReportName} - Page ${page}.xls`"
                  :bulkname="`${downloadReportName}.xls`"
                  :fetch="fetchMerchantInactivationReportNoPagination"
                  @refresh="fetchMerchantInactivationReport"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="items"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div class="sortable-header">
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #cell(last_order_created_at)="data">
              {{ __dateTimeFormatter(data.value) || '---' }}
            </template>
            <template #cell(last_order_dispatched_at)="data">
              {{ __dateTimeFormatter(data.value) || '---' }}
            </template>
            <template #cell(inactivated_at)="data">
              {{ __dateTimeFormatter(data.value) || '---' }}
            </template>
            <template #cell(inactive_days)="data">
              {{ __dateTimeFormatterPassedTime(data.item.inactivated_at) || '---' }}
            </template>
            <template #cell(inactive_status)="data">
              <b-badge :variant="data.value === 'auto' ? 'light-primary' : 'light-warning'">
                {{ data.value === 'auto' ? 'Auto' : 'Manual' }}
              </b-badge>
            </template>
          </b-table>
        </b-overlay>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label class="mr-1">Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block width-125"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total)? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BBadge,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BPagination,
} from 'bootstrap-vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'

const ReportRepository = RepositoryFactory.get('report')

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    vSelect,
    AdvancedDateFilter,
    BBadge,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BOverlay,
    BPagination,
    FilterButtons,
    FilterCard,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      inactivationMethodOptions: [
        { label: 'Auto', value: 'auto' },
        { label: 'Manual', value: 'manual' },
      ],
      filters: {
        inactivated_at: {
          startDate: null,
          endDate: null,
        },
        last_order_dispatched_at: {
          startDate: null,
          endDate: null,
        },
      },
      tableData: [],
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      fields: [
        {
          key: 'ref_no',
          label: 'Merchant ID',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'name',
          label: 'Merchant Name',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'last_order_created_at',
          label: 'Last Order Date',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'last_order_dispatched_at',
          label: 'Last Dispatched Date',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'inactivated_at',
          label: 'Inactivated Date',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'inactive_days',
          label: 'No of Inactive Days',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'inactive_status',
          label: 'Inactivation Method',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
      ],
      json_fields: {
        'Merchant ID': 'ref_no',
        'Merchant Name': {
          field: 'name',
          callback: value => `"${value}"`,
        },
        'Last Order Date': {
          field: 'last_order_created_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Last Dispatched Date': {
          field: 'last_order_dispatched_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Inactivated Date': {
          field: 'inactivated_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'No of Inactive Days': {
          field: 'inactivated_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatterPassedTime(value),
        },
        'Inactivation Method': 'inactive_status',
      },
      downloadReportName: 'Merchant-inactivation-report',
      items: [],
    }
  },
  computed: {
    sortOptions() {
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchMerchantInactivationReport()
      },
    },
    deep: true,
    page() {
      this.fetchMerchantInactivationReport()
    },
    perPage() {
      this.fetchMerchantInactivationReport()
    },
  },
  mounted() {
    this.totalRows = this.items.length
    this.setThisMonthFilter()
    this.fetchMerchantInactivationReport()
    this.updateFilterCount()
  },
  methods: {
    async fetchMerchantInactivationReport() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await ReportRepository.getMerchantInactivationReport(this.page, filterQuery, this.perPage))
        this.items = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchMerchantInactivationReportNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await ReportRepository.getMerchantInactivationReportNoPagination(filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    setThisMonthFilter() {
      const currentMonth = this.monthFilter()
      this.updateDateRange('inactivated_at', currentMonth)
    },
    applyFilters() {
      this.fetchMerchantInactivationReport()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {
        inactivated_at: {
          startDate: null,
          endDate: null,
        },
        last_order_dispatched_at: {
          startDate: null,
          endDate: null,
        },
      }
      this.setThisMonthFilter()
      this.$nextTick(() => {
        this.fetchMerchantInactivationReport()
      })
      this.updateFilterCount()
    },
  },
}
</script>
<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select';
</style>
